<!-- /* * Confidential and Proprietary. * Do not distribute without 1-800-Flowers.com, Inc. consent. *
Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved. */ -->

<div class="chat-container">
	{#if $chatVisible}
		<div in:fade={{ duration: 150 }} class="widget-wrapper">
			<ChatWidget
				{typing}
				{chatInitialized}
				{initChat}
				{restartConversation}
				{clickedSuggestions}
				{modalId}
				{showModal}
				{clickedOrders}
			/>
		</div>
	{:else}
		<div in:fade={{ duration: 100 }} class="button-wrapper">
			<ChatButton {initChat} {restartConversation} />
		</div>
	{/if}
</div>

<script lang="ts">
	import { writable, type Writable } from 'svelte/store';
	import { fade } from 'svelte/transition';
	import {
		chatSession,
		chatVisible,
		connectedToAgent,
		entryMessage,
		is_start_loading,
		notificationSoundEnabled,
		updateSessionStorage
	} from '$lib/stores/chatSessionStore';
	import ChatWidget from '$lib/components/chat/ChatWidget.svelte';
	import ChatButton from '$lib/components/chat/ChatButton.svelte';
	import { startConversation } from '$lib/apis/webchat/startConversation';
	import { sendMessage } from '$lib/apis/webchat/sendMessage';
	import { soundNotification } from '$lib/utilities/soundNotification';

	export let clickedSuggestions: { [key: string]: boolean };
	export let clickedOrders: { [key: string]: string };
	export let restartConversation: () => Promise<void>;
	let chatInitialized = writable(false);
	let typing = writable(false);
	const { play } = soundNotification('./ding.mp3');

	export let modalId: Writable<string>;
	export let showModal: Writable<boolean>;

	const initChat = async () => {
		chatVisible.update(() => true);
		if (!$chatSession.conversation_id) {
			if (['header', 'global'].includes($chatSession.entry_point)) {
				typing.update(() => true);
				// if we are waiting on the start endpoint to get our conversation id and set the store, do NOT start another process. return out
				if ($is_start_loading) return;
				else is_start_loading.set(true);

				const returnValue = await startConversation({
					token: $chatSession?.token,
					browser_uuid: $chatSession.browseruuid,
					channel: $chatSession?.channel,
					entry_point: $chatSession.entry_point,
					entry_page_type: $chatSession.entry_page_type,
					brand: $chatSession?.brand,
					firstName: $chatSession?.firstName || '',
					lastName: $chatSession?.lastName || '',
					email: $chatSession?.email || '',
					agent_direct: { origin: '' },
					contactId: $chatSession?.contactId,
					enterprise_id: $chatSession?.enterpriseId || ''
				});
				if (returnValue?.conversation_id) {
					typing.update(() => false);
					chatSession.update((old) => {
						const updatedSession = {
							...old,
							conversation_id: returnValue?.conversation_id as string,
							agent: returnValue?.message?.params?.agent as 'flora' | 'sierra' | 'decagon',
							messages: [
								...old.messages,
								{
									sender: 'bot',
									message: returnValue?.message?.message as string,
									type: 'text',
									params: returnValue?.message?.params as Record<string, unknown>
								}
							]
						};
						return updatedSession;
					});
					if ($notificationSoundEnabled) play();
				} else {
					modalId.update(() => 'start_api_error');
					showModal.update(() => true);
				}
				if (returnValue.message.params.trigger === 'transfer_exec') {
					connectedToAgent.set(true);
				}
			} else {
				chatSession.update((old) => {
					const updatedSession = {
						...old,
						messages: [
							...old.messages,
							{ sender: 'customer', message: $entryMessage?.suggestion, type: 'text' }
						]
					};
					sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
					return updatedSession;
				});
				typing.update(() => true);
				// if we are waiting on the start endpoint to get our conversation id and set the store, do NOT start another process. return out
				if ($is_start_loading) return;
				else is_start_loading.set(true);

				const returnValue = await startConversation({
					token: $chatSession?.token,
					browser_uuid: $chatSession.browseruuid,
					channel: $chatSession?.channel,
					entry_point: $chatSession.entry_point,
					entry_page_type: $chatSession.entry_page_type,
					brand: $chatSession?.brand,
					firstName: $chatSession?.firstName || '',
					lastName: $chatSession?.lastName || '',
					email: $chatSession?.email || '',
					agent_direct: { origin: '' },
					contactId: $chatSession?.contactId,
					enterprise_id: $chatSession?.enterpriseId || ''
				});
				if (returnValue?.conversation_id) {
					typing.update(() => false);

					chatSession.update((old) => {
						const updatedSession = {
							...old,
							conversation_id: returnValue?.conversation_id as string,
							agent: returnValue?.message?.params?.agent as 'flora' | 'sierra' | 'decagon'
						};
						return updatedSession;
					});
					updateSessionStorage({ ...$chatSession });
					typing.update(() => true);
					const initialMessage = await sendMessage(
						{
							conversation_id: $chatSession.conversation_id,
							brand_code: $chatSession.brand,
							channel: $chatSession.channel,
							message: $entryMessage.suggestion,
							type: 'text',
							params: {
								typing: false,
								passport: {
									passportStatus: !!$chatSession?.passportEndDate
								},
								customer: {
									email: $chatSession.email,
									contact_id: $chatSession.contactId,
									browser_uuid: $chatSession.browseruuid,
									first: $chatSession.firstName,
									last: $chatSession.lastName
								},
								selected_option: {
									type: 'suggestions',
									suggestions: $entryMessage
								}
							}
						},
						$chatSession?.token
					);
					typing.update(() => false);
					chatSession.update((old) => {
						const updatedSession = {
							...old,
							messages: [
								...old.messages,
								{
									sender: 'bot',
									message: initialMessage?.message,
									type: initialMessage.type,
									params: initialMessage.params || {}
								}
							],
							chatHistory: initialMessage.params.conversation_history || old.chatHistory
						};
						sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
						return updatedSession;
					});
					if ($notificationSoundEnabled) play();
					sessionStorage.removeItem('entryMessage');
					entryMessage.set({ suggestion: '', type: '' });
					chatInitialized.set(true);
				} else {
					modalId.update(() => 'start_api_error');
					showModal.update(() => true);
				}
				if (returnValue.message.params.trigger === 'transfer_exec') {
					connectedToAgent.set(true);
				}
			}
			// we finished initialization logic. No longer loading.
			is_start_loading.set(false);
		}
	};
</script>

<style>
	.chat-container {
		position: fixed;
		bottom: 0;
		right: 0;
		z-index: 1000;
		pointer-events: none;
	}
	
	.button-wrapper {
		pointer-events: auto; 
		position: absolute;
		bottom: 0;
		right: 0;
	}
	
	.widget-wrapper {
		pointer-events: auto;
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
	}
</style>


